/**
 * The fallback — what the assistant says when no intent owns the question.
 *
 * The regex engine answered every unmatched question with the same fleet
 * overview, which read as a confident answer to a question nobody asked (it
 * is how "which department owns this?" came back as a stock table's cousin).
 * Now the miss is said out loud: "here's what I can answer", grouped by
 * family, and the three nearest intents' example prompts as one-click
 * actions. The fleet overview is still there — ask for it by name.
 *
 * "Nearest" is word overlap between the question and each intent's examples,
 * plus any partial score the router gave it.
 *
 * Engine layer — pure.
 */

import type { AnswerKit } from './answer-kit.ts';
import type { OemAnswer, OemAnswerAction } from './answer-types.ts';
import { INTENT_BY_ID, INTENTS } from './intents/index.ts';
import type { IntentDef } from './intents/types.ts';
import type { PageContext } from './page-context.ts';
import type { ScoredIntent } from './router.ts';

const STOP = new Set([
  'the', 'a', 'an', 'is', 'are', 'what', 'which', 'who', 'how', 'why', 'do', 'does', 'did',
  'we', 'i', 'our', 'us', 'of', 'to', 'in', 'on', 'at', 'for', 'and', 'or', 'it', 'this',
  'that', 'me', 'my', 'be', 'can', 'should', 'will', 'would', 'have', 'has', 'with', 'there',
]);

const words = (s: string): string[] =>
  (s.toLowerCase().match(/[a-z0-9$-]+/g) ?? []).filter((w) => w.length > 1 && !STOP.has(w));

/** Families, in the order the help lists them, with what each covers. */
export const FAMILY_HELP: readonly (readonly [string, string])[] = [
  ['Fleet & risk', 'which machines are at risk (by site or class), hot spots, breakdown trends, how far to trust a prediction'],
  ['Machines', "a machine's history, why it failed, what to change next and what it costs, its telemetry"],
  ['Cost', 'cost per operating hour by mine, crew or model, the cost of doing nothing, whether air freight is worth it'],
  ['Parts', 'stock at a site, lines below minimum, where a part sits in the network, what is holding a job up'],
  ['Work', 'approvals and what waiting costs, PMs due in a window, agent-drafted work orders, a module\'s queue'],
  ['Service', 'technician dispatch, certificates, customer ratings, remote support, contract renewals'],
  ['Operations & reliability', 'root cause and who an observation goes to (operator or equipment), which operators need coaching, who was driving, which machine to target first, SPC, TARP levels, FMEA and control plans'],
  ['Documents', 'a bulletin or procedure by id, every mention of a phrase'],
  ['Reports', 'weekly status, the risk email, a site deck, the S.O.S report'],
];

/** The three intents nearest a question. */
export function nearestIntents(q: string, scored: readonly ScoredIntent[], page: PageContext): IntentDef[] {
  const qw = new Set(words(q));
  const partial = new Map(scored.map((s) => [s.id, s.score]));
  const usable = (i: IntentDef) =>
    i.id !== 'machine_lookup' && (!i.needs?.includes('module') || !!page.module);
  return INTENTS.filter(usable)
    .map((i) => {
      const overlap = Math.max(
        ...i.examples.map((e) => {
          const ew = words(e);
          return ew.filter((w) => qw.has(w)).length / Math.max(1, Math.min(ew.length, qw.size || 1));
        }),
      );
      return { i, s: overlap * 3 + (partial.get(i.id) ?? 0) };
    })
    .filter((x) => x.s > 0)
    .sort((a, b) => b.s - a.s || b.i.priority - a.i.priority)
    .slice(0, 3)
    .map((x) => x.i);
}

/** Offered when nothing is near: the executive path — risk, cost, the overview. */
const DEFAULT_NEAREST = ['risk_ranking', 'cost_of_inaction', 'fleet_overview'];

export function fallbackAnswer(
  q: string,
  scored: readonly ScoredIntent[],
  page: PageContext,
  kit: AnswerKit,
): OemAnswer {
  const near = nearestIntents(q, scored, page);
  const pick = [...near];
  for (const id of DEFAULT_NEAREST) {
    if (pick.length >= 3) break;
    if (!pick.some((i) => i.id === id)) pick.push(INTENT_BY_ID[id]);
  }
  const actions: OemAnswerAction[] = pick.map((i) => ({ label: i.examples[0], prompt: i.examples[0] }));
  const markdown =
    `I can't answer that from the workspace data — I read the fleet register, work orders, parts, telemetry and documents, and I don't guess.\n\n` +
    `**Here's what I can answer:**\n\n` +
    FAMILY_HELP.map(([f, what]) => `- **${f}** — ${what}`).join('\n') +
    `\n\nThe closest questions I can answer are below — or ask for a **fleet overview**.`;
  return kit.done(markdown, [], { actions });
}
